import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type {
  CoachingContentV3,
  CriterionCoachingContent,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import { getOutcomeImpactCopy } from "@repo/app-types/helpers/outcomeImpactCopy";
import { IconFlagCheck } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { renderStructuredContent } from "../structuredContent/structuredContentRenderer.js";
import { CoachingGoalSummary } from "./coachingGoalSummary.js";
import { CoachingScoreTargetBar } from "./coachingScoreTargetBar.js";

type CoachingNoteCriterionContent = CriterionCoachingContent & {
  content: CoachingContentV3;
};

interface TeammateCoachingNoteProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  /** The single focus criterion the note coaches on. */
  criterion: PlaybookCriterionNode;
  /** Stored coaching content for the focus criterion (goal, net conversions). */
  criterionContent: CoachingNoteCriterionContent;
  /** The rep's current weighted average on the criterion, or null with no calls. */
  currentScore: number | null;
  /** Whether the current score is still loading (suppresses the no-calls note). */
  isCurrentScoreLoading: boolean;
  /** The playbook's outcome definition, used to name the impact outcome. */
  outcomeDefinition: OutcomeDefinition | undefined;
  /** The coaching-period selector, rendered in the card header. */
  periodSelector: ReactNode;
  /**
   * Optional manager note (or its editor), rendered between the goal summary
   * and the AI coaching note body.
   */
  managerNoteSlot?: ReactNode;
}

/**
 * Renders the rep-facing coaching note for the modern single-note format. The
 * goal summary panel and note body reuse the same progress and V3 AST rendering
 * primitives as the rest of the coaching experience.
 */
export const TeammateCoachingNote: FC<TeammateCoachingNoteProps> = ({
  playbookId,
  teammateId,
  criterion,
  criterionContent,
  currentScore,
  isCurrentScoreLoading,
  outcomeDefinition,
  periodSelector,
  managerNoteSlot,
}) => {
  const goal = criterionContent.goal;
  const noteContent = criterionContent.content;

  const netConversions = criterionContent.net_conversions ?? 0;
  const impactCopy = getOutcomeImpactCopy({
    netConversions,
    // The note format doesn't carry average revenue per opp; revenue outcomes
    // fall back to a generic "increase revenue" sentence (see getOutcomeImpactCopy).
    averageRevenuePerOpp: undefined,
    outcomeNoun: outcomeDefinition
      ? getOutcomeNoun(outcomeDefinition)
      : undefined,
    outcomePluralNoun: outcomeDefinition?.plural_noun,
    isRevenueOutcome: outcomeDefinition
      ? isRevenueOutcomeDefinition(outcomeDefinition)
      : undefined,
  });

  return (
    <div className="w-full rounded-lg border border-border-light bg-white shadow-xs">
      <div className="space-y-4 p-4">
        {/* Card header: title + coaching-period selector */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <IconFlagCheck className="size-4 shrink-0 text-emerald-700" />
            <p className="text-sm font-semibold text-foreground">
              Weekly coaching goal
            </p>
          </div>
          {periodSelector}
        </div>

        <div className="rounded-lg border border-border-light bg-gray-50 p-4">
          <p className="text-xs font-medium text-muted-foreground">
            {criterion.name}
          </p>

          {goal?.type === "score_target" ? (
            <div className="mt-4">
              <CoachingScoreTargetBar
                currentScore={currentScore}
                target={goal.score_target}
                gradingValueType={criterion.grading_value_type}
                isLoading={isCurrentScoreLoading}
              />
            </div>
          ) : goal ? (
            <div className="mt-4">
              <CoachingGoalSummary
                goal={goal}
                goalProgress={criterionContent.goal_progress}
              />
            </div>
          ) : null}

          {/* Outcome-impact line */}
          {netConversions > 0 ? (
            <p className="mt-4 text-xs leading-4 font-medium text-muted-foreground">
              {impactCopy.isRevenue ? (
                <>
                  Improve here to{" "}
                  <span className="font-semibold text-emerald-700">
                    {impactCopy.sentence}
                  </span>
                  .
                </>
              ) : (
                <>
                  Improve here to drive{" "}
                  <span className="font-semibold text-emerald-700">
                    ~{Math.round(netConversions).toLocaleString("en-US")} more{" "}
                    {impactCopy.pluralNoun}
                  </span>{" "}
                  per 1,000 calls.
                </>
              )}
            </p>
          ) : null}
        </div>

        {managerNoteSlot}

        {/* The cohesive freeform note, rendered from CoachingContentV3. */}
        <div className="max-w-[46rem] text-sm text-foreground">
          {renderStructuredContent(noteContent, {
            teammateId,
            playbookId,
            criterionId: criterion.id,
          })}
        </div>
      </div>
    </div>
  );
};
